import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Spinner } from './EnvLayout';
import { ui, errText, answerLabel, type Answer } from './envUi';

/** Neutralise spreadsheet formulas and quote a CSV cell. */
function csvCell(v: string | number): string {
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/** Everyone who completed the survey, with every answer. */
export default function AdminResponses() {
  const utils = trpc.useUtils();
  const { data: attempts, isLoading, error } = trpc.questionnaire.adminAttempts.useQuery();
  const del = trpc.questionnaire.deleteAttempt.useMutation({ onSuccess: () => utils.questionnaire.adminAttempts.invalidate() });
  const [open, setOpen] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !attempts) return <p className="text-red-600 dark:text-red-400">{error ? errText(error) : 'Could not load responses.'}</p>;

  const totalAgree    = attempts.reduce((n, a) => n + a.agrees, 0);
  const totalDisagree = attempts.reduce((n, a) => n + a.disagrees, 0);

  const exportCsv = () => {
    const header = ['Name', 'Number', 'Major', 'Student ID', 'Agree Count', 'Disagree Count', 'Date', 'Answers'];
    const lines = attempts.map(a => [
      a.name, a.phone ?? '', a.major, a.studentId, a.agrees, a.disagrees,
      new Date(a.createdAt).toLocaleString(),
      (a.answers as Answer[]).map(x => `${x.en} => ${answerLabel(x.type, x.answer)}`).join(' | '),
    ].map(csvCell).join(','));
    const csv = '﻿' + [header.map(csvCell).join(','), ...lines].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'environmental_club_attempts.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section>
      <div className="mb-5 grid grid-cols-3 gap-3">
        <Stat label="Participants" value={attempts.length} cls="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100" />
        <Stat label="Total agree" value={totalAgree} cls="bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400" />
        <Stat label="Total disagree" value={totalDisagree} cls="bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400" />
      </div>

      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-bold">Participants ({attempts.length})</h2>
        <button onClick={exportCsv} disabled={attempts.length === 0} className={ui.btnGhost}>Export CSV</button>
      </div>

      <ul className="flex flex-col gap-2">
        {attempts.length === 0 && <li className={`${ui.muted} py-6 text-center`}>No responses yet.</li>}
        {attempts.map(a => {
          const isOpen = open === a.id;
          return (
            <li key={a.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <button onClick={() => setOpen(isOpen ? null : a.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{a.name}</p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {a.phone ?? 'no number'} · {a.major} · ID {a.studentId} · {new Date(a.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="shrink-0 text-sm font-bold">
                  <span className="text-green-600 dark:text-green-400">+{a.agrees}</span>{' '}
                  <span className="text-red-600 dark:text-red-400">-{a.disagrees}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-3">
                  <ul className="flex flex-col gap-2">
                    {(a.answers as Answer[]).map((x, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-slate-700 dark:text-slate-200">{i + 1}. {x.en}</span>
                        <span className={`shrink-0 font-bold ${x.answer === 'agree' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {answerLabel(x.type, x.answer)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {confirmId === a.id ? (
                      <>
                        <span className="text-xs text-slate-500 dark:text-slate-400">Delete this response?</span>
                        <button className={ui.btnDanger} onClick={() => { del.mutate({ id: a.id }); setConfirmId(null); }}>Yes</button>
                        <button className={ui.btnGhost} onClick={() => setConfirmId(null)}>No</button>
                      </>
                    ) : (
                      <button className={ui.btnDanger} onClick={() => setConfirmId(a.id)}>Delete</button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Stat({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className={`rounded-2xl p-3 text-center ${cls}`}>
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}
